/**
 * Utility per tracciare le interazioni utente inviando eventi al data layer `window.digitalData`.
 * L'helper è pensato per essere richiamato sia da HTML inline (`onclick`) sia da altre funzioni JS.
 */

/**
 * Garantisce l'esistenza di un array `window.digitalData` e lo restituisce.
 * @returns {Array|null}
 */
function getDigitalDataLayer() {
    if (typeof window === 'undefined') {
        console.warn('[tracking] window non disponibile: evento non tracciato.');
        return null;
    }

    if (!window.digitalData) {
        window.digitalData = [];
    } else if (typeof window.digitalData.push !== 'function') {
        console.warn('[tracking] `window.digitalData` non è un array compatibile con push.');
        return null;
    }

    return window.digitalData;
}

/**
 * Normalizza le informazioni dell'evento applicando fallback e valorizzando i campi mancanti.
 * @param {Object} params
 * @param {HTMLElement} [params.element] Elemento da cui provengono i dati (opzionale).
 * @param {string} [params.event]
 * @param {string} [params.eventDetailName]
 * @param {string} [params.eventDetailType]
 * @param {string} [params.componentType]
 * @param {string} [params.componentTitle]
 * @param {string} [params.componentLink]
 * @param {string} [params.componentLinkText]
 * @returns {{event: string, eventDetail: {name: string, type?: string}, component: {type: string, title: string, link: string, linkText: string}}}
 */
function buildPayload(params = {}) {
    const {
        element,
        event = '',
        eventDetailName = '',
        eventDetailType = '',
        componentType = '',
        componentTitle = '',
        componentLink = '',
        componentLinkText = ''
    } = params;

    const dataset = element && element.dataset ? element.dataset : {};

    const payload = {
        event: event || dataset.trackingEvent || '',
        eventDetail: {
            name: eventDetailName || dataset.trackingEventName || ''
        },
        component: {
            type: componentType || dataset.componentType || '',
            title: componentTitle || dataset.componentTitle || element?.getAttribute?.('title') || '',
            link: componentLink || dataset.componentLink || (element && element.href) || '',
            linkText: componentLinkText || dataset.componentLinkText || (element && element.textContent ? element.textContent.trim() : '')
        }
    };

    const detailType = eventDetailType || dataset.trackingEventType || '';
    if (detailType) {
        payload.eventDetail.type = detailType;
    }

    return payload;
}

/**
 * Traccia un evento utente pushando i dati sul data layer `digitalData`.
 *
 * Uso tipico:
 *  - inline HTML: onclick="trackUserInteraction({ event: 'internalLink', eventDetailName: 'internal_link', componentLink: this.href }, this)"
 *  - JS: import { trackUserInteraction } from './tracking'; trackUserInteraction({ event: 'internalLink', ... , element: btn });
 *
 * Se viene passato `element`, i campi mancanti vengono ricavati automaticamente dall'elemento (dataset, href, title, testo).
 *
 * @param {Object} params - configurazione dell'evento.
 * @param {HTMLElement} [element] - elemento sorgente (quando non fornito via params.element).
 * @returns {object|null} payload inviato o null se il push non è stato eseguito.
 */
export function trackUserInteraction(params = {}, element) {
    const layer = getDigitalDataLayer();
    if (!layer) {
        return null;
    }

    const options = params && typeof params === 'object' ? { ...params } : {};

    if (!options.element && element instanceof HTMLElement) {
        options.element = element;
    }

    const payload = buildPayload(options);

    if (!payload.event) {
        console.warn('[tracking] Evento non inviato: campo `event` mancante.');
        return null;
    }

    if (!payload.eventDetail.name) {
        console.warn('[tracking] Evento non inviato: campo `eventDetail.name` mancante.');
        return null;
    }

    layer.push(payload);
    return payload;
}

/**
 * Helper per collegare il tracking ad un set di elementi tramite selector CSS.
 * I valori indicati nelle data-attribute hanno precedenza sui parametri passati.
 *
 * @param {string} selector
 * @param {Object} defaults
 */
export function bindTrackingToElements(selector, defaults = {}) {
    if (typeof document === 'undefined') {
        return;
    }

    const elements = Array.from(document.querySelectorAll(selector));
    elements.forEach((el) => {
        el.addEventListener('click', () => {
            trackUserInteraction({ ...defaults, element: el });
        });
    });
}

// Espone la funzione a livello globale per l'utilizzo via attributo inline `onclick`.
if (typeof window !== 'undefined') {
    window.trackUserInteraction = trackUserInteraction;
    window.bindTrackingToElements = bindTrackingToElements;
}

export default trackUserInteraction;
